import React from 'react';
import { AlertTriangle, Check, CircleSlash, Sparkles, X } from 'lucide-react';

/**
 * The parts of a screening scorecard the profile never showed.
 *
 * The profile used to answer "what is on this resume" — contact details, a flat
 * list of skills, a list of past roles. It never answered the question a
 * recruiter actually opens it with: "how much of what we asked for is here?"
 * These four blocks answer that, all from data the backend already returns.
 *
 * `requirement_coverage` is computed per request against the job as it stands,
 * so a requirement edited on the job shows up here without a re-score.
 */

const GREEN = 'var(--green)';
const RED = 'var(--red)';
const MUTED = 'var(--text-muted)';

/** A stated requirement can be met, missed, or never stated at all — three states, not two. */
function verdictColor(meets) {
  if (meets === null || meets === undefined) return MUTED;
  return meets ? GREEN : RED;
}

function Tile({ label, value, unit, note, notes, color }) {
  const lines = (notes && notes.length) ? notes : (note ? [note] : []);
  return (
    <div style={{
      flex: '1 1 150px', minWidth: 150,
      background: 'var(--bg-secondary)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', padding: '12px 14px',
    }}>
      <div style={{
        fontSize: 10.5, fontWeight: 600, letterSpacing: '.06em',
        textTransform: 'uppercase', color: MUTED,
      }}>{label}</div>
      <div style={{
        marginTop: 6, display: 'flex', alignItems: 'baseline', gap: 5,
        fontSize: 22, fontWeight: 700, color: color || 'var(--text-primary)',
        fontFamily: 'var(--font-mono)',
      }}>
        {value}
        {unit && <span style={{ fontSize: 12, fontWeight: 500, color: MUTED, fontFamily: 'var(--font)' }}>{unit}</span>}
      </div>
      {lines.map((line, i) => (
        <div
          key={`${line}-${i}`}
          style={{
            marginTop: i === 0 ? 6 : 4,
            fontSize: 11.5,
            fontWeight: 600,
            color: color || MUTED,
            lineHeight: 1.45,
          }}
        >
          {line}
        </div>
      ))}
    </div>
  );
}

/** Must-haves, nice-to-haves, experience and education at a glance. */
export function RequirementCoverage({ coverage }) {
  if (!coverage || !coverage.must_have) return null;
  const { must_have: must, nice_to_have: nice, certifications: certs, experience: exp, education: edu } = coverage;

  const mustColor = must.total === 0 ? MUTED : must.met === must.total ? GREEN : must.pct >= 70 ? 'var(--orange)' : RED;
  const expLabel = exp.required == null
    ? 'No bar set'
    : exp.years == null ? 'Not on resume'
      : exp.years >= exp.required + 2 ? 'Exceeds' : exp.meets ? 'Meets' : 'Below bar';

  return (
    <div className="card" style={{ gridColumn: '1 / -1' }}>
      <div className="section-title">Requirement coverage</div>
      <div style={{ fontSize: 12.5, color: MUTED, marginTop: -6, marginBottom: 14 }}>
        Measured against this job's requirements as they stand now.
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <Tile
          label="Must-haves met" value={`${must.met}`} unit={`/ ${must.total}`}
          note={must.total === 0 ? 'None specified' : must.met === must.total ? 'All met' : `${must.total - must.met} missing`}
          color={mustColor}
        />
        <Tile
          label="Nice-to-haves" value={`${nice.met}`} unit={`/ ${nice.total}`}
          note={nice.total === 0 ? 'None specified' : `${nice.pct}% covered · not scored`}
          color={nice.total === 0 ? MUTED : nice.met > 0 ? GREEN : MUTED}
        />
        <Tile
          label="Experience"
          value={exp.years == null ? '—' : exp.years}
          unit={exp.required == null ? 'yrs' : `yrs · req ${exp.required}+`}
          note={expLabel} color={verdictColor(exp.meets)}
        />
        {/* Education lists qualifications; scoring is handled on Resume Score. */}
        <Tile
          label="Education"
          value={!edu.value ? 'Missing' : edu.meets === null ? 'Stated' : edu.meets ? 'Meets' : 'Differs'}
          unit={edu.required ? `req ${edu.required}` : (edu.items?.length ? `${edu.items.length} listed` : '')}
          notes={edu.items?.length ? edu.items : [edu.value || 'Not stated on the resume']}
          color={edu.value && edu.meets ? GREEN : MUTED}
        />
        {certs.total > 0 && (
          <Tile
            label="Certifications" value={`${certs.met}`} unit={`/ ${certs.total}`}
            note={certs.met === certs.total ? 'All held' : `${certs.total - certs.met} missing · up to -3 marks`}
            color={certs.met === certs.total ? GREEN : 'var(--orange)'}
          />
        )}
      </div>
    </div>
  );
}

function ChipRow({ items, tone }) {
  const style = tone === 'missing'
    ? { background: 'var(--red-bg)', color: RED, borderColor: 'rgba(217,45,32,0.25)' }
    : { background: 'var(--green-bg)', color: GREEN, borderColor: 'rgba(14,159,110,0.25)' };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {items.map((s) => (
        <span key={s} style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 12, padding: '3px 9px', borderRadius: 999,
          border: '1px solid', ...style,
        }}>
          {tone === 'missing' ? <X size={11} strokeWidth={2.6} /> : <Check size={11} strokeWidth={2.6} />}
          {s}
        </span>
      ))}
    </div>
  );
}

/**
 * Which required skills are evidenced and which are not.
 *
 * The plain skills list treats every skill as equal. This one only cares about
 * the ones the job asked for, and — more usefully — names what is absent,
 * which a list of what someone *has* can never show.
 */
export function SkillCoverage({ coverage, skills }) {
  if (!coverage || !coverage.must_have) return null;
  const { must_have: must, nice_to_have: nice } = coverage;
  const required = new Set([...must.matched, ...must.missing, ...nice.matched, ...nice.missing]);
  const extras = (skills || []).filter((s) => !required.has(s));
  const empty = required.size === 0 && extras.length === 0;

  return (
    <div className="card" style={{ gridColumn: '1 / -1' }}>
      <div className="section-title">Skill coverage</div>

      {empty && (
        <div style={{ fontSize: 13, color: MUTED }}>
          {(skills || []).length === 0
            ? 'No skills were parsed from this resume.'
            : 'This job lists no required or preferred skills to compare against.'}
        </div>
      )}

      {must.matched.length > 0 && (
        <>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: MUTED, margin: '4px 0 7px' }}>
            REQUIRED — EVIDENCED ({must.matched.length})
          </div>
          <ChipRow items={must.matched} tone="found" />
        </>
      )}

      {must.missing.length > 0 && (
        <>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: MUTED, margin: '14px 0 7px' }}>
            REQUIRED — NOT EVIDENCED ({must.missing.length})
          </div>
          <ChipRow items={must.missing} tone="missing" />
        </>
      )}

      {(nice.matched.length > 0 || nice.missing.length > 0) && (
        <>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: MUTED, margin: '14px 0 7px' }}>
            NICE TO HAVE ({nice.met}/{nice.total})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {nice.matched.map((s) => (
              <span key={s} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12,
                padding: '3px 9px', borderRadius: 999, border: '1px solid rgba(14,159,110,0.25)',
                background: 'var(--green-bg)', color: GREEN,
              }}><Check size={11} strokeWidth={2.6} />{s}</span>
            ))}
            {nice.missing.map((s) => (
              <span key={s} className="tag" style={{ color: 'var(--text-faint)' }}>{s}</span>
            ))}
          </div>
        </>
      )}

      {extras.length > 0 && (
        <>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: MUTED, margin: '14px 0 7px' }}>
            ALSO ON THE RESUME ({extras.length})
          </div>
          <div className="tags-wrap">
            {extras.map((s) => <span key={s} className="tag">{s}</span>)}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Employment history on a rail rather than as a flat list.
 *
 * Same records the profile always had, ordered so the shape of a career — how
 * long each step lasted, whether it is progression or churn — is readable
 * without doing arithmetic on the dates.
 */
export function ExperienceTimeline({ history }) {
  const roles = history || [];
  if (roles.length === 0) return null;
  const longest = Math.max(...roles.map((h) => Number(h.years) || 0), 1);

  return (
    <div className="card" style={{ gridColumn: '1 / -1' }}>
      <div className="section-title">Experience timeline</div>
      <div style={{ position: 'relative', paddingLeft: 20, marginTop: 4 }}>
        <div style={{
          position: 'absolute', left: 5, top: 8, bottom: 12,
          width: 2, background: 'var(--border)',
        }} />
        {roles.map((h, i) => {
          const years = Number(h.years) || 0;
          return (
            <div key={i} style={{ position: 'relative', paddingBottom: i === roles.length - 1 ? 0 : 18 }}>
              <div style={{
                position: 'absolute', left: -19, top: 5, width: 10, height: 10,
                borderRadius: '50%', background: i === 0 ? 'var(--accent-2)' : 'var(--border-light)',
                border: '2px solid var(--bg-card)', boxShadow: '0 0 0 1.5px var(--border)',
              }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{h.role}</div>
                <span style={{ fontSize: 12, color: MUTED, fontFamily: 'var(--font-mono)' }}>{h.duration}</span>
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 1 }}>{h.company}</div>
              {years > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  <div style={{
                    height: 5, borderRadius: 3, background: 'var(--gradient)',
                    width: `${Math.max(6, (years / longest) * 100)}%`, maxWidth: 320, opacity: i === 0 ? 1 : 0.45,
                  }} />
                  <span style={{ fontSize: 11.5, color: MUTED, fontFamily: 'var(--font-mono)' }}>{years} yrs</span>
                </div>
              )}
              {(h.key_responsibilities || []).length > 0 && (
                <div style={{ fontSize: 12.5, color: MUTED, marginTop: 5, lineHeight: 1.6 }}>
                  {h.key_responsibilities.join(' · ')}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const EDU_BULLETS = /[\u2022\u2023\u25E6\u2043\u2219\u00B7\uF0B7\uF0A7\u25CF\u25CB\u25AA•▪◦●]+/g;

function cleanEducationText(value) {
  return String(value || '').replace(EDU_BULLETS, ' ').replace(/\s+/g, ' ').trim().replace(/^[-|•]+/, '').trim();
}

function formatEducationEntry(entry) {
  if (!entry) return '';
  if (typeof entry === 'string') return cleanEducationText(entry);
  const parts = [entry.degree, entry.field, entry.institution].map(cleanEducationText).filter(Boolean);
  const year = entry.year && Number(entry.year) ? String(entry.year) : '';
  const line = parts.join(', ');
  return year ? (line ? `${line} · ${year}` : year) : line;
}

function educationLines(education) {
  const rows = [];
  for (const entry of education || []) {
    const formatted = formatEducationEntry(entry);
    if (!formatted) continue;
    const bits = formatted.split(/\s*(?:\n|;|\u2022||•)\s*/).map(cleanEducationText).filter(Boolean);
    if (bits.length > 1) rows.push(...bits);
    else rows.push(formatted);
  }
  return rows;
}

/** One qualification per row. PDF bullets and a flattened blob both split here. */
export function EducationList({ education, items }) {
  const rows = (items && items.length) ? items : educationLines(education);
  return (
    <div className="card">
      <div className="section-title">Education</div>
      {rows.length === 0 ? (
        <span style={{ color: MUTED, fontSize: 13 }}>None listed</span>
      ) : rows.map((line, i) => (
        <div
          key={`${line}-${i}`}
          style={{
            padding: '9px 0',
            borderBottom: i === rows.length - 1 ? 'none' : '1px solid var(--border)',
            fontSize: 13,
            lineHeight: 1.55,
            color: 'var(--text-primary)',
          }}
        >
          {line}
        </div>
      ))}
    </div>
  );
}

/** The projects listed on the resume, with the technologies each one used. */
export function ProjectsList({ projects }) {
  const items = (projects || []).filter((p) => p && (p.name || p.description));
  return (
    <div className="card" style={{ gridColumn: '1 / -1' }}>
      <div className="section-title">Projects</div>
      {items.length === 0 ? (
        <span style={{ color: MUTED, fontSize: 13 }}>None listed on the resume</span>
      ) : items.map((p, i) => (
        <div key={i} style={{ padding: '10px 0', borderBottom: i === items.length - 1 ? 'none' : '1px solid var(--border)' }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name || 'Untitled project'}</div>
          {p.description && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3, lineHeight: 1.6 }}>{p.description}</div>
          )}
          {(p.technologies || []).length > 0 && (
            <div className="tags-wrap" style={{ marginTop: 6 }}>
              {p.technologies.map((t) => <span key={t} className="tag">{t}</span>)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Why the score landed where it did.
 *
 * The scorer has always produced these; they were discarded before reaching the
 * database, so the profile could show a number with no reasoning attached.
 */
export function StrengthsAndRisks({ strengths, gaps, scoredBy, scored }) {
  const s = strengths || [];
  const g = gaps || [];
  if (s.length === 0 && g.length === 0) {
    if (!scored) return null;
    return (
      <div className="card" style={{ gridColumn: '1 / -1' }}>
        <div className="section-title">Strengths &amp; risks</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: MUTED }}>
          <CircleSlash size={15} strokeWidth={1.8} />
          Scored before this was recorded — re-score to capture the reasoning.
        </div>
      </div>
    );
  }

  const List = ({ items, icon, color }) => (
    <div>
      {items.map((item, i) => (
        <div key={i} style={{
          display: 'flex', gap: 8, alignItems: 'flex-start',
          fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6,
          padding: '7px 0', borderBottom: '1px solid var(--border)',
        }}>
          <span style={{ color, flexShrink: 0, marginTop: 2 }}>{icon}</span>
          {item}
        </div>
      ))}
    </div>
  );

  return (
    <div className="card" style={{ gridColumn: '1 / -1' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <div className="section-title" style={{ marginBottom: 0 }}>Strengths &amp; risks</div>
        {scoredBy && (
          <span style={{ fontSize: 11.5, color: MUTED }}>
            Screened by {scoredBy === 'fallback' ? 'rule-based fallback' : 'AI'}
            {scoredBy === 'fallback' && ' — AI was unavailable, treat with care'}
          </span>
        )}
      </div>
      <div className="grid-2" style={{ marginTop: 12 }}>
        <div>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: MUTED, marginBottom: 2 }}>STRENGTHS</div>
          {s.length ? <List items={s} icon={<Sparkles size={13} strokeWidth={2} />} color={GREEN} />
            : <div style={{ fontSize: 13, color: MUTED, padding: '7px 0' }}>None recorded.</div>}
        </div>
        <div>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: MUTED, marginBottom: 2 }}>
            RISKS &amp; GAPS <span style={{ fontWeight: 400 }}>· for reference, not scored</span>
          </div>
          {g.length ? <List items={g} icon={<AlertTriangle size={13} strokeWidth={2} />} color="var(--orange)" />
            : <div style={{ fontSize: 13, color: MUTED, padding: '7px 0' }}>None recorded.</div>}
        </div>
      </div>
    </div>
  );
}

const BREAKDOWN_LABELS = {
  technical_skills_match: 'Must-haves met',
  years_of_experience: 'Experience',
  projects: 'Projects',
  domain_industry_fit: 'Domain / industry',
  career_progression: 'Career progression',
  education_certifications: 'Education & certifications',
  location_budget_fit: 'Work mode, location & budget',
};

export function breakdownLabel(key) {
  return BREAKDOWN_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

/** Arithmetic behind the resume score — counted vs skipped. */
export function ScoreCalculationModal({ calculation, savedScore, onClose }) {
  if (!calculation) return null;
  const counted = calculation.counted || [];
  const skipped = calculation.skipped || [];
  const total = calculation.total ?? 0;
  const max = calculation.max ?? 100;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 24,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-card)', borderRadius: 16, padding: 28,
          width: '100%', maxWidth: 480, border: '1px solid var(--border)',
          maxHeight: '90vh', overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Score calculation</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
              Must-haves and experience always count. Education and certifications
              count only when they are on the resume or required by the job. Nice-to-haves are skipped.
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close" style={{ padding: 6 }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: MUTED, margin: '16px 0 8px' }}>
          Counted
        </div>
        {counted.map((row) => (
          <div key={row.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{row.label}</div>
              <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{row.formula || row.detail}</div>
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>
              {row.score}/{row.max}
            </div>
          </div>
        ))}

        <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: MUTED, margin: '18px 0 8px' }}>
          Not scored
        </div>
        {skipped.map((row) => (
          <div key={row.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{row.label}</div>
            <div style={{ fontSize: 12, color: MUTED }}>{row.detail}</div>
          </div>
        ))}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Total</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: 22, color: total >= 75 ? GREEN : 'var(--text-primary)' }}>
            {total}/{max}
          </div>
        </div>
        {savedScore != null && savedScore !== total && (
          <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>
            Saved score was {savedScore}. This total uses must-haves and experience as they stand now.
          </div>
        )}
        <button className="btn btn-primary" onClick={onClose} style={{ width: '100%', marginTop: 18 }}>
          Close
        </button>
      </div>
    </div>
  );
}
